<template>
  <Form>
    <template #header>
      {{
        `${$t("projects.form.firstStep.labelFirstPart")} ${mode} ${$t(
          "projects.form.firstStep.labelLastPart"
        )}`
      }}
    </template>

    <div class="is-label">
      {{ $t("projects.form.firstStep.projectType")
      }}<AuTooltip
        :description="`<div>Voting project used to collect ${defaultAutMnemonic} as vote weight to determine which project is preferable for furter investment.</div> <div>After completion or decline vote fund will be returned to investor back.</div>`"
      />
    </div>
    <div class="is-indent">
      <AuSelect
        :value="projectType"
        :options="prepareSmartContractsTypes"
        required
        clearable
        :placeholder="$t('projects.form.firstStep.projectTypePlaceholder')"
        @input="(value) => (projectType = value)"
      />
    </div>
    <div class="is-naming">
      <AuInput
        v-model="projectNameEng"
        :label="$t('projects.form.firstStep.projectNameLabel')"
        :tooltip="'The short name of your project. (at least 10 characters)'"
        :has-error="projectNameEngHasError"
        class="input-name"
        :placeholder="$t('projects.form.firstStep.projectNamePlaceholder')"
        required
        clearable
      />
      <AuInput
        :label="$t('projects.form.firstStep.projectAbbrNameLabel')"
        :model-value="projectAbbrNameEng"
        :has-error="projectAbbrNameEngHasError"
        :maxlength="8"
        class="input-abbr"
        :placeholder="$t('projects.form.firstStep.projectAbbrNamePlaceholder')"
        required
        clearable
        @update:modelValue="setProjectAbbrNameEng"
      />
    </div>

    <div class="is-indent">
      <AuSelect
        :select-label="$t('Country')"
        :value="country"
        :options="countryOptions"
        :placeholder="$t('companies.form.countryPlaceholder') + '(*)'"
        required
        @input="value => country = value"
      />
    </div>

    <div class="is-naming">
      <AuInput
        v-model="realEstateType"
        class="input-name"
        :label="$t('Real estate type')"
        :placeholder="$t('Short description')"
        clearable
      />
      <AuInput
        v-model="district"
        class="input-abbr"
        :label="$t('District')"
        :placeholder="$t('Enter district')"
        clearable
      />
    </div>

    <div class="is-naming">
      <AuInput
        v-model="city"
        :label="$t('City')"
        class="input-name"
        :placeholder="$t('Enter city')"
        clearable
      />
      <AuInput
        v-model="street"
        :label="$t('Street')"
        class="input-abbr"
        :placeholder="$t('Enter street')"
        clearable
      />
    </div>
    <div class="is-naming">
      <AuInput
        v-model="developer"
        :label="$t('Developer')"
        class="input-name"
        :placeholder="$t('Enter developer')"
        clearable
      />
      <AuInput
        v-model="tourUrl"
        :label="$t('Tour url')"
        class="input-abbr"
        :placeholder="$t('Enter tour url')"
        clearable
      />
    </div>
    <div class="is-naming">
      <AuInput
        v-model="totalAreaMt"
        :label="$t('Total area sq.m.')"
        class="input-name"
        :placeholder="$t('Enter total area in meters')"
        clearable
        type="Number"
      />
      <AuInput
        v-model="totalAreaFt"
        :label="$t('Total area ft.')"
        class="input-abbr"
        :placeholder="$t('Enter total area in feet')"
        clearable
        type="Number"
      />
    </div>

    <div class="is-indent">
      <AuInput
        v-model="shortDescription"
        :label="$t('projects.form.firstStep.shortDescriptionLabel')"
        :placeholder="$t('projects.form.firstStep.shortDescriptionPlaceholder')"
        required
        clearable
        text-area-heigth="75"
        type="TextArea"
      />
    </div>
    <div class="is-label">
      {{ $t("projects.form.firstStep.projectDescLabel") }}
    </div>
    <!-- <div class="is-indent">
      <AuInput
        v-model="projectDescriptionEng"
        type="TextArea"
        text-area-heigth="75"
        required
        clearable
        :placeholder="$t('projects.form.firstStep.projectDescEngPlaceholder')"
      />
    </div> -->
    <div class="is-indent">
      <AuMarkdown
        v-model="projectDescriptionEng"
        :required="true"
        :placeholder="$t('projects.form.firstStep.projectDescEngPlaceholder')"
        :show-view="false"
      />
    </div>

    <AuInput
      v-model="address"
      :label="$t('projects.form.firstStep.projectEntityAddress')"
      :tooltip="$t('projects.form.firstStep.projectEntityAddressTooltip')"
      class="is-indent"
      :placeholder="$t('projects.form.firstStep.projectEntityAddress')"
      clearable
    />

    <div class="is-indent">
      <AuInput
        v-model="tokensCount"
        :label="$t('projects.form.firstStep.projectTokensCountLabel')"
        :has-error="tokensCountHasError"
        required
        :placeholder="
          $t('projects.form.firstStep.projectTokensCountPlaceholder')
        "
        type="NullableNumber"
      />
    </div>
    <div class="is-label">
      {{ $t("projects.form.firstStep.projectCompanyLabel") }}
    </div>
    <div class="is-indent">
      <AuSelect
        :value="company"
        :options="companiesOptions"
        :placeholder="$t('projects.form.firstStep.projectCompanyPlaceholder')"
        required
        clearable
        re-select
        @input="(value) => (company = value)"
        @clearValue="company = null"
      />
    </div>
    <div
      v-if="isTestRole"
      class="is-label"
    >
      {{ $t("projects.form.firstStep.projectPlatformLabel") }}
      <AuTooltip
        :description="$t('projects.form.firstStep.projectPlatformTooltip')"
      />
    </div>
    <div
      v-if="isTestRole"
      class="is-indent"
    >
      <AuSelect
        :value="externalPlatform"
        :options="platformOptions"
        :placeholder="$t('projects.form.firstStep.projectPlatformPlaceholder')"
        required
        clearable
        re-select
        @input="(value) => (externalPlatform = value)"
        @clearValue="externalPlatform = null"
      />
    </div>

    <div class="is-label">
      {{ $t("projects.form.firstStep.applicationType") }}
    </div>
    <div class="is-indent">
      <AuSelect
        :value="applicationType"
        :options="applicationTypeOptions"
        :placeholder="
          $t('projects.form.firstStep.projectApplicationTypePlaceholder')
        "
        clearable
        re-select
        @input="(value) => (applicationType = value)"
      />
    </div>
    <div
      v-if="projectType === 'invest' || projectType === 'invest_v2'"
      class="checkbox"
    >
      <input
        id="openSale"
        v-model="hasOpenSale"
        type="checkbox"
      >
      <label for="openSale">
        {{ $t("projects.form.firstStep.hasOpenSale") }}
      </label>
    </div>

    <div v-if="apiSystemSettings?.enableUserLevels == '1'">
      <div class="is-label">
        {{ $t("projects.form.firstStep.minUserLevel") }}
      </div>
      <div class="is-indent">
        <AuSelect
          :value="requiredUserStatus"
          :options="userLevelOptions"
          :placeholder="$t('projects.form.firstStep.minUserLevelPlaceholder')"
          re-select
          @input="(value) => (requiredUserStatus = value)"
        />
      </div>
    </div>
    <div v-if="applicationType == 'pool'">
      <div class="is-label">
        {{ $t("projects.form.firstStep.riskType") }}
      </div>
      <div class="is-indent">
        <AuSelect
          :value="risk"
          :options="riskOptions"
          :placeholder="
            $t('projects.form.firstStep.projectRiskTypePlaceholder')
          "
          clearable
          re-select
          @input="(value) => (risk = value)"
        />
      </div>
    </div>

    <!-- <div class="is-label">
      {{ $t('projects.form.firstStep.projectCryptoTypeLabel') }}
    </div>
    <div class="is-indent">
      <AuSelect
        :value="cryptoActiveType"
        :options="cryptoActiveTypeOptions"
        required
        clearable
        disabled
        :placeholder="$t('projects.form.firstStep.projectCryptoTypePlaceholder')"
        @input="changeCryptoActiveType"
      />
    </div>
    <div
      v-if="showInvestType"
      class="is-label"
    >
      {{ $t('projects.form.firstStep.projectInvestTypeLabel') }}
    </div>
    <div
      v-if="showInvestType"
      class="is-indent"
    >
      <AuSelect
        :value="investType"
        :options="investmentTypeOptions"
        required
        clearable
        disabled
        :placeholder="$t('projects.form.firstStep.projectInvestTypePlaceholder')"
        @input="value => investType = value"
      />
    </div>
    <div
      v-if="showIsVoiting"
      class="is-label"
    >
      {{ $t('projects.form.firstStep.projectVoitingTypeLabel') }}
    </div>
    <div
      v-if="showIsVoiting"
      class="is-indent"
    >
      <AuSelect
        :value="isVoiting"
        :options="isVoitingOptions"
        required
        clearable
        :placeholder="$t('projects.form.firstStep.projectVoitingTypePlaceholder')"
        @input="value => isVoiting = value"
      />
    </div> -->

    <div
      v-if="isTestRole"
      class="is-indent is-extended"
      @click="showExtended = !showExtended"
    >
      <span>
        {{ $t("projects.form.firstStep.projectExtended") }}
      </span>
      <AuIcon
        :icon="showExtended ? 'chevron_up' : 'chevron_down'"
        clickable
        @click="showExtended = !showExtended"
      />
    </div>
    <div
      v-if="showExtended"
      class="is-hidden-block is-indent"
    >
      <div class="is-test-type">
        <div
          v-if="isFieldVisible('minExpertVotes', projectType)"
          class="test-left"
        >
          <AuInput
            v-model="minExpertVotes"
            :label="$t('projects.form.firstStep.minExpertVotes')"
            class="input-name-left"
            :placeholder="
              $t('projects.form.firstStep.minExpertVotesPlaceholder')
            "
            type="NullableNumber"
          />
        </div>

        <div
          v-if="isFieldVisible('issuerCommision', projectType)"
          class="test-right"
        >
          <AuInput
            v-model="issuerCommision"
            :label="$t('projects.form.firstStep.issuerCommision')"
            class="input-abbr-right"
            :placeholder="
              $t('projects.form.firstStep.issuerCommisionPlaceholder')
            "
            type="NullableNumber"
            :max="100"
          />
        </div>
      </div>

      <div class="checkbox">
        <input
          id="burn"
          v-model="burnUnsoldTokens"
          type="checkbox"
        >
        <label for="burn">{{
          $t("projects.form.firstStep.projectBurnUnsoldTokens")
        }}</label>
      </div>
      <div class="checkbox">
        <input
          id="autodeployTokens"
          v-model="autodeployTokens"
          type="checkbox"
        >
        <label for="autodeployTokens">{{
          $t("projects.form.firstStep.projectAutodeploy")
        }}</label>
      </div>

      <div
        v-if="isFieldVisible('hasEscrow', projectType)"
        class="checkbox"
      >
        <input
          id="hasEscrow"
          v-model="hasEscrow"
          type="checkbox"
        >
        <label for="hasEscrow">{{
          $t("projects.form.firstStep.projectHasEscrow")
        }}</label>
      </div>

      <div class="checkbox">
        <input
          id="makeVisible"
          v-model="makeVisible"
          type="checkbox"
        >
        <label for="makeVisible">{{
          $t("projects.form.firstStep.projectMakeVisible")
        }}</label>
      </div>
    </div>

    <div
      v-if="showError"
      class="error"
    >
      {{ errorMsg }}
    </div>

    <template #footer>
      <AuButton
        type="primary"
        center
        @click="nextStep"
      >
        {{ $t("projects.form.nextButton") }}
      </AuButton>
    </template>
  </Form>
</template>

<script>
import _ from "lodash";
import AuTooltip from "@/components/extended/AuTooltip.vue";
import BigNumber from "bignumber.js";
import isFieldVisible from "@/help/typesProjects";
import { mapActions, mapGetters } from "vuex";

export default {
  components: { AuTooltip },
  props: {
    id: {
      type: String,
      default: "new"
    },
    firstPartOfProjectInfo: {
      type: Object,
      default: () => {}
    }
  },
  setup() {
    return {
      isFieldVisible
    };
  },
  data() {
    return {
      realEstateType: null,
      country: null,
      district: null,
      city: null,
      street: null,
      developer: null,
      tourUrl: null,
      totalAreaMt: null,
      totalAreaFt: null,
      projectNameEng: null,
      address: null,
      projectNameEngHasError: false,
      projectAbbrNameEng: null,
      projectAbbrNameEngHasError: false,
      shortDescription: "",
      projectDescriptionRus: "",
      projectDescriptionEng: "",
      tokensCount: "",
      tokensCountHasError: false,
      company: null,
      externalPlatform: null,
      applicationType: "common",
      hasOpenSale: false,
      requiredUserStatus:
        this.apiSystemSettings?.userLimitStatuses[0] || "base",
      risk: "",
      projectType: "invest_v2",
      cryptoActiveType: "investment",
      investType: "non-redeemable",
      isVoiting: false,
      errorMsg: null,
      showError: false,
      showExtended: false,
      burnUnsoldTokens: true,
      autodeployTokens: false,
      makeVisible: false,
      hasEscrow: false,
      cryptoActiveTypeOptions: [
        {
          display: this.$t("projects.cryptoActiveTypes.investment"),
          value: "investment"
        },
        {
          display: this.$t("projects.cryptoActiveTypes.access"),
          value: "access"
        },
        {
          display: this.$t("projects.cryptoActiveTypes.ticket"),
          value: "ticket"
        },
        {
          display: this.$t("projects.cryptoActiveTypes.voucher"),
          value: "voucher"
        },
        {
          display: this.$t("projects.cryptoActiveTypes.management"),
          value: "management"
        }
      ],
      investmentTypeOptions: [
        {
          display: this.$t("projects.investType.redeemable"),
          value: "redeemable"
        },
        {
          display: this.$t("projects.investType.non-redeemable"),
          value: "non-redeemable"
        }
      ],
      minExpertVotes: "",
      issuerCommision: "",
      platforms: []
    };
  },

  computed: {
    ...mapGetters({
      countrys: "country",
      language: "language",
      companies: "companies",
      defaultAutMnemonic: "defaultToken/defaultAutMnemonic",
      profile: "profile/profile",
      isTestRole: "profile/isTestRole",
      smartContractsTypes: "smartContractsTypes",
      apiSystemSettings: "apiSystemSettings"
    }),

    countryOptions() {
      return _.map(this.countrys, c => ({ display: c.name, value: c.code }));
    },

    loginAsTester() {
      return this.isTestRole;
    },

    autoDeployEnv() {
      return process.env.VUE_APP_AUTODEPLOY_PROJECT == "true";
    },

    showIsVoiting() {
      return this.investType == "non-redeemable";
    },

    showInvestType() {
      return this.cryptoActiveType == "investment";
    },

    companiesOptions() {
      const options = _.map(this.companies, (company) => ({
        display: this.language === "ru" ? company.nameRus : company.nameEng,
        value: `/api/companies/${company.id}`
      }));
      return _.sortBy(options, "display");
    },

    platformOptions() {
      return _.map(this.platforms, (platform) => ({
        display: platform.name,
        value: `/api/external_platforms/${platform.id}`
      }));
    },

    applicationTypeOptions() {
      return [
        {
          display: this.language == "ru" ? "Общий" : "Common",
          value: "common"
        },
        {
          display: this.language == "ru" ? "Доход (аренда)" : "Income (Rent)",
          value: "income "
        },
        {
          display: this.language == "ru" ? "Рост капитала" : "Capital Growth",
          value: "growth"
        },
        {
          display: this.language == "ru" ? "Контроль рисков" : "Risk Control",
          value: "risk"
        },
        {
          display: this.language == "ru" ? "Пул" : "Pool",
          value: "pool"
        },
        {
          display: this.language == "ru" ? "Пул ставок" : "Staking pool",
          value: "stakingPool"
        },
        {
          display: this.language == "ru" ? "Основной" : "Foundation",
          value: "foundation"
        }
      ];
    },

    userLevelOptions() {
      return Object.values(this.apiSystemSettings?.userLimitStatuses).map(
        (level) => ({
          display: level.charAt(0).toUpperCase() + level.slice(1).toLowerCase(),
          value: level
        })
      );
    },

    riskOptions() {
      return [
        {
          display: this.language == "ru" ? "Консервативный" : "Conservative",
          value: "conservative"
        },
        {
          display: this.language == "ru" ? "Сбалансированный" : "Balanced",
          value: "balanced"
        },
        {
          display: this.language == "ru" ? "Агрессивный" : "Agressive",
          value: "agressive"
        }
      ];
    },

    mode() {
      return this.id === "new"
        ? this.$t("projects.form.firstStep.labelNew")
        : this.$t("projects.form.firstStep.labelEdit");
    },

    isVoitingOptions() {
      return [
        {
          display: this.$t("projects.form.firstStep.yes"),
          value: true
        },
        {
          display: this.$t("projects.form.firstStep.no"),
          value: false
        }
      ];
    },

    requiredFields() {
      return [
        {
          label: this.$t("projects.form.firstStep.projectNameLabel"),
          value: this.projectNameEng
        },
        {
          label: this.$t("projects.form.firstStep.projectAbbrNameLabel"),
          value: this.projectAbbrNameEng
        },
        {
          label: this.$t("projects.form.firstStep.projectDescLabel"),
          value: this.projectDescriptionEng
        },
        {
          label: this.$t("projects.form.firstStep.projectTokensCountLabel"),
          value: this.tokensCount
        },
        {
          label: this.$t("projects.form.firstStep.projectCompanyLabel"),
          value: this.company
        }
      ];
    },

    prepareSmartContractsTypes() {
      const options = this.smartContractsTypes.map((item) => ({
        display: this.getTranslatedTitle(item.title),
        value: item.type
      }));
      return _.sortBy(options, "display");
    }
  },

  watch: {
    companies() {
      const options = this.companiesOptions;
      if (options.length == 1 && !this.company) {
        this.company = options[0].value;
      }
    }
  },

  async mounted() {
    if (_.isEmpty(this.countrys)) {
      await this.getCountry();
    }
    this.makeVisible = this.loginAsTester;
    if (this.firstPartOfProjectInfo) {
      this.realEstateType = this.firstPartOfProjectInfo?.realEstateType;
      this.country = this.firstPartOfProjectInfo?.country;
      this.district = this.firstPartOfProjectInfo?.district;
      this.city = this.firstPartOfProjectInfo?.city;
      this.street = this.firstPartOfProjectInfo?.street;
      this.developer = this.firstPartOfProjectInfo?.developer;
      this.tourUrl = this.firstPartOfProjectInfo?.tourUrl;
      this.totalAreaMt = this.firstPartOfProjectInfo?.totalAreaMt;
      this.totalAreaFt = this.firstPartOfProjectInfo?.totalAreaFt;
      this.projectNameEng = this.firstPartOfProjectInfo.projectNameEng;
      this.address = this.firstPartOfProjectInfo.address;
      this.projectAbbrNameEng = this.firstPartOfProjectInfo.projectAbbrNameEng;
      this.shortDescription = this.firstPartOfProjectInfo.shortDescription;
      this.projectDescriptionRus =
        this.firstPartOfProjectInfo.projectDescriptionEng;
      this.projectDescriptionEng =
        this.firstPartOfProjectInfo.projectDescriptionEng;
      this.tokensCount = this.firstPartOfProjectInfo.tokensCount;
      this.company = this.firstPartOfProjectInfo.company;
      this.externalPlatform = this.firstPartOfProjectInfo.externalPlatform;
      this.applicationType =
        this.firstPartOfProjectInfo.applicationType || "common";
      this.hasOpenSale = this.firstPartOfProjectInfo?.hasOpenSale;
      this.requiredUserStatus =
        this.firstPartOfProjectInfo?.requiredUserStatus || "base";
      this.risk = this.firstPartOfProjectInfo.risk;
      this.cryptoActiveType = this.firstPartOfProjectInfo.cryptoActiveType;
      this.projectType = this.firstPartOfProjectInfo.projectType;
      this.burnUnsoldTokens = this.firstPartOfProjectInfo.burnUnsoldTokens;
      this.autodeployTokens = this.firstPartOfProjectInfo.autodeployTokens;
      this.makeVisible = this.firstPartOfProjectInfo.makeVisible ? false : true;
      this.hasEscrow = this.firstPartOfProjectInfo.hasEscrow;
      this.minExpertVotes = this.firstPartOfProjectInfo?.minExpertVotes ?? "";
      this.issuerCommision = this.firstPartOfProjectInfo?.issuerCommision ?? "";

      if (this.firstPartOfProjectInfo.investType) {
        this.investType = this.firstPartOfProjectInfo.investType;
      }
      if (this.showIsVoiting) {
        this.isVoiting = this.firstPartOfProjectInfo.isVoiting;
      }
    }
    else {
      this.autodeployTokens = this.autoDeployEnv;
    }


    if (_.isEmpty(this.companies)) {
      await this.getCompanies();
      if (this.companiesOptions.length === 1 && !this.company) {
        this.company = this.companiesOptions[0]?.value;
      }
    }
    if (this.id !== "new" && !this.firstPartOfProjectInfo) {
      const requestToIssue = await this.getRequestToIssue(this.id);
      this.setFields(requestToIssue);
    }

    const options = this.companiesOptions;
    if (options.length == 1) {
      this.company = options[0].value;
    }
    await this.loadPlatforms();
  },

  methods: {
    ...mapActions({
      getCountry: "getCountry",
      getHydraRequestToIssue: "getHydraRequestToIssue",
      getCompanies: "getCompanies",
      getPlatforms: "platforms/getPlatforms",
      getRequestToIssue: "getRequestToIssue"
    }),

    async loadPlatforms() {
      const params = {
        page: 1,
        itemsPerPage: 30,
        status: "success"
      };
      try {
        const resp = await this.getPlatforms(params);
        this.platforms = resp.data;
      }
      catch (error) {
        this.$toast.error(
          error.response.data?.message || error.response.data?.detail
        );
      }
    },

    onInput(event, fieldName, maxValue) {
      let cleanedValue = event.target.value.replace(/[^0-9]/g, "");
      if (cleanedValue === "") {
        this[fieldName] = "";
        return;
      }
      if (maxValue) {
        cleanedValue = Math.min(Number(cleanedValue), maxValue).toString();
      }
      this[fieldName] = cleanedValue;
    },

    checkFieldValue(value) {
      return /^[+-]?\d+([.,]\d+)?$/.test(value) ? Number(value) : null;
    },

    setProjectAbbrNameEng(value) {
      this.projectAbbrNameEng = value.toUpperCase();
    },

    checkRequiredFields() {
      const res = _.find(
        this.requiredFields,
        (field) => !field.value || field.value === ""
      );
      this.errorMsg = res
        ? this.$t("projects.form.error") + ` ${res.label}`
        : null;
      return res === undefined;
    },

    checkProjectNameEng() {
      const regexStartWithLetter = /^[a-zA-Z]+.*$/;
      const foundStartWithLetter =
        this.projectNameEng.match(regexStartWithLetter);
      if (!foundStartWithLetter) {
        this.errorMsg = "The name must start with a latin letter";
        return false;
      }

      this.projectNameEngHasError = false;
      return true;
    },

    checkProjectAbbrNameEng() {
      const regexStartWithLetter = /^[a-zA-Z]+.*$/;
      const foundStartWithLetter =
        this.projectAbbrNameEng.match(regexStartWithLetter);
      if (!foundStartWithLetter) {
        this.errorMsg =
          "The token symbol / mnemonic must start with a latin letter";
        return false;
      }

      const regexContainLatin = /^[a-zA-Z]+[a-zA-Z\d ]*$/;
      const foundContainLatin =
        this.projectAbbrNameEng.match(regexContainLatin);
      if (!foundContainLatin) {
        this.errorMsg =
          "The token symbol / mnemonic must contain only latin characters and numbers";

        return false;
      }

      this.projectAbbrNameEngHasError = false;
      return true;
    },

    nextStep() {
      let firstPartOfProjectInfo = {
        projectNameEng: this.projectNameEng,
        realEstateType: this.realEstateType,
        country: this.country,
        district: this.district,
        city: this.city,
        street: this.street,
        developer: this.developer,
        tourUrl: this.tourUrl,
        totalAreaMt: this.totalAreaMt,
        totalAreaFt: this.totalAreaFt,
        address: this.address,
        projectAbbrNameEng: this.projectAbbrNameEng,
        shortDescription: this.shortDescription,
        projectDescriptionRus: this.projectDescriptionEng,
        projectDescriptionEng: this.projectDescriptionEng,
        tokensCount: this.tokensCount,
        company: this.company,
        externalPlatform: this.externalPlatform,
        applicationType: this.applicationType || "common",
        hasOpenSale: this.hasOpenSale,
        requiredUserStatus: this.requiredUserStatus || "base",
        risk: this.risk,
        projectType: this.projectType,
        cryptoActiveType: this.cryptoActiveType,
        burnUnsoldTokens: this.burnUnsoldTokens,
        autodeployTokens: this.autodeployTokens,
        makeVisible: this.makeVisible ? false : true,
        minExpertVotes: this.checkFieldValue(this.minExpertVotes),
        issuerCommision: this.checkFieldValue(this.issuerCommision),
        hasEscrow: this.hasEscrow
      };
      if (this.showInvestType) {
        firstPartOfProjectInfo.investType = this.investType;
      }
      if (this.showIsVoiting) {
        firstPartOfProjectInfo.isVoiting = this.isVoiting;
      }

      if (this.checkRequiredFields()) {
        if (!this.checkProjectNameEng()) {
          this.showError = true;
          this.projectNameEngHasError = true;
          return;
        }

        if (!this.checkProjectAbbrNameEng()) {
          this.showError = true;
          this.projectAbbrNameEngHasError = true;
          return;
        }

        if (!BigNumber(this.tokensCount).isInteger()) {
          this.showError = true;
          this.tokensCountHasError = true;
          this.errorMsg = this.$t(
            "projects.form.firstStep.errorTokenCountNoInteger"
          );
          return;
        }

        this.$emit("firstPartOfProject", firstPartOfProjectInfo);
        this.$emit("nextStep");
      }
      else {
        this.showError = true;
      }
    },

    changeCryptoActiveType(value) {
      this.cryptoActiveType = value;
      this.investType = null;
      this.isVoiting = null;
    },

    goBack() {
      this.$router.push({ name: "my projects" });
    },

    setFields(data) {
      if (!data) {
        return;
      }
      this.realEstateType = data?.realEstateType;
      this.country = data?.country;
      this.district = data?.district;
      this.city = data?.city;
      this.street = data?.street;
      this.developer = data?.developer;
      this.tourUrl = data?.tourUrl;
      this.totalAreaMt = data?.totalAreaMt;
      this.totalAreaFt = data?.totalAreaFt;
      this.projectNameEng = data.tokenNameEng;
      this.address = data.address;
      this.projectAbbrNameEng = data.tokenMnemonicEng;
      this.shortDescription = data.shortDescription;
      this.projectDescriptionRus = data.descriptionCapitalEng;
      this.projectDescriptionEng = data.descriptionCapitalEng;
      this.tokensCount = data.tokenCount;
      this.company = data.company;
      this.externalPlatform = data.externalPlatform;
      this.applicationType = data.applicationType || "common";
      this.hasOpenSale = data.hasOpenSale;
      this.requiredUserStatus = data?.requiredUserStatus || "base";
      this.risk = data.risk;
      this.projectType = data.projectType;
      this.cryptoActiveType = data.cryptoActiveType;
      this.burnUnsoldTokens = data.burnUnsoldTokens;
      this.autodeployTokens = data?.autoProjectCreate;
      this.makeVisible = data?.visibility ? false : true;
      this.hasEscrow = data.hasEscrow;
      this.minExpertVotes = data?.minExpertVotes ?? "";
      this.issuerCommision = data?.issuerCommision ?? "";
      if (this.showInvestType) {
        this.investType = data.investType;
      }
      if (this.showIsVoiting) {
        this.isVoiting = data.isVoiting;
      }
    },

    getTranslatedTitle(title) {
      switch (title) {
        case "Голосующий":
          return this.$t("projects.projectTypes.voting");
        case "Mudaraba":
          return this.$t("projects.projectTypes.mudaraba");
        case "Musharaka":
          return this.$t("projects.projectTypes.musharaka");
        case "Инвестиционный":
          return this.$t("projects.projectTypes.invest");
        default:
          return title;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.error {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  color: red;
}

.is1 {
  font-family: "Suisse Intl", serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  padding: 20px;
}
.is-label {
  margin-bottom: 8px;
  color: var(--color-50);
}
.is-line {
  width: 50%;
}
.is-indent {
  margin-bottom: 24px;
}
.is-small-indent {
  margin-bottom: 15px;
}
.is-next {
  border: 1px dashed #289566;
  border-radius: 14px;
  padding: 12px;
  &:hover {
    cursor: pointer;
  }
}

.is-test-type {
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
  width: 100%;
  gap: 20px 10px;
  @media screen and (max-width: 1000px) {
    flex-direction: column;
  }
}
.test-left {
  width: 100%;
  .label-name-left {
    margin-bottom: 7px;
  }
}
.test-right {
  width: 100%;
  .label-abbr-right {
    margin-bottom: 7px;
  }
}

.is-naming {
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "a b" "c d";
  gap: 0px 10px;
  margin-bottom: 24px;

  .label-name {
    grid-area: a;
  }
  .label-abbr {
    grid-area: b;
  }
  .input-name {
    grid-area: c;
    @media screen and (max-width: 1000px) {
      margin-bottom: 14px;
    }
  }
  .input-abbr {
    grid-area: d;
  }

  @media screen and (max-width: 1000px) {
    grid-template-areas: "a a" "c c" "b b" "d d";
  }
}
.is-extended {
  cursor: pointer;
  display: flex;
  justify-content: center;
  span {
    font-size: 14px;
    text-decoration: underline;
  }
}
.is-hidden-block {
  display: flex;
  flex-direction: column;
  input:hover {
    cursor: pointer;
  }
  label:hover {
    cursor: pointer;
  }
}
.checkbox {
  display: flex;
  gap: 8px;
  align-items: center;
}
</style>
